import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Building2, MapPin, Hash, ChevronDown, ChevronUp, Search, Filter,
  ExternalLink, Briefcase, Award, FileText, RefreshCw, Users, Loader2,
  ChevronLeft, ChevronRight
} from 'lucide-react';

const PAGE_SIZE = 10;

const CompanySpine = ({
  profileId,
  loadCompanies,
  loadCompanyCorroboration,
  loadGrantSources,
}) => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [corroboration, setCorroboration] = useState(null);
  const [corroborationLoading, setCorroborationLoading] = useState(false);
  const [grantSources, setGrantSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPkd, setFilterPkd] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [currentPage, setCurrentPage] = useState(1);

  const loadData = useCallback(async () => {
    if (!profileId) return;
    setLoading(true);
    setError(null);
    try {
      const [companiesData, grantSourcesData] = await Promise.all([
        loadCompanies(profileId),
        loadGrantSources(profileId),
      ]);
      setCompanies(companiesData || []);
      setGrantSources(grantSourcesData || []);
      setCurrentPage(1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [profileId, loadCompanies, loadGrantSources]);

  useEffect(() => { loadData(); }, [loadData]);

  const refresh = () => {
    setSelectedCompany(null);
    setCorroboration(null);
    loadData();
  };

  const toggleCompany = async (company) => {
    if (selectedCompany?.id === company.id) {
      setSelectedCompany(null);
      setCorroboration(null);
      return;
    }
    setSelectedCompany(company);
    setCorroboration(null);
    setCorroborationLoading(true);
    try {
      const data = await loadCompanyCorroboration(company.id, profileId);
      setCorroboration(data);
    } catch (err) {
      setCorroboration({ error: err.message, matches: [], grant_history: [] });
    } finally {
      setCorroborationLoading(false);
    }
  };

  // Derive unique PKD codes for filter dropdown
  const uniquePkds = [...new Set(
    companies.map(c => c.primary_pkd).filter(Boolean)
  )].sort();

  // Filter and sort
  const filteredCompanies = companies
    .filter(c => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const nameMatch = (c.company_name || '').toLowerCase().includes(q);
        const cityMatch = (c.city || '').toLowerCase().includes(q);
        const nidMatch = (c.national_id || '').toLowerCase().includes(q);
        if (!nameMatch && !cityMatch && !nidMatch) return false;
      }
      if (filterPkd && c.primary_pkd !== filterPkd) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return (a.company_name || '').localeCompare(b.company_name || '');
      if (sortBy === 'city') return (a.city || '').localeCompare(b.city || '');
      if (sortBy === 'created_at') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      return 0;
    });

  // Stats
  const totalCompanies = companies.length;
  const activeCompanies = companies.filter(c => c.status === 'active').length;
  const withGrants = companies.filter(c => (c.grant_count || 0) > 0).length;
  const withSignals = companies.filter(c => (c.signal_count || 0) > 0).length;

  // Pagination
  const totalPages = Math.ceil(filteredCompanies.length / PAGE_SIZE) || 1;
  const paginatedCompanies = filteredCompanies.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-3 text-gray-500">Loading company spine…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-2">Failed to load company data</div>
        <p className="text-sm text-gray-400 mb-4">{error}</p>
        <button
          onClick={refresh}
          className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary-600" />
            Company Intelligence Spine
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {companies.length} companies tracked across all intelligence countries
          </p>
        </div>
        <button
          onClick={refresh}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Companies', value: totalCompanies, icon: Building2, color: 'text-primary-600', bg: 'bg-primary-50' },
          { label: 'Active', value: activeCompanies, icon: Users, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'With Grants', value: withGrants, icon: Award, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'With Signals', value: withSignals, icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' },
        ].map(stat => (
          <div key={stat.label} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className={`p-1.5 rounded-lg ${stat.bg}`}>
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
              </div>
              <span className="text-xs text-gray-500">{stat.label}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Search + filter bar */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search companies by name, city, or NIP…"
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <select
            value={filterPkd}
            onChange={e => { setFilterPkd(e.target.value); setCurrentPage(1); }}
            className="pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none cursor-pointer"
          >
            <option value="">All PKD codes</option>
            {uniquePkds.map(pkd => (
              <option key={pkd} value={pkd}>{pkd}</option>
            ))}
          </select>
        </div>
        <select
          value={sortBy}
          onChange={e => { setSortBy(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none cursor-pointer"
        >
          <option value="name">Sort by Name</option>
          <option value="city">Sort by City</option>
          <option value="created_at">Sort by Newest</option>
        </select>
      </div>

      {/* Grant Sources section */}
      {grantSources.length > 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <h4 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
            <Award className="w-4 h-4" />
            Grant Sources
          </h4>
          {grantSources.map(gs => (
            <div key={gs.id} className="flex items-center justify-between py-2 border-b border-amber-100 last:border-0">
              <div>
                <span className="font-medium text-sm text-gray-900">{gs.source_name}</span>
                {gs.programme_name && (
                  <span className="text-xs text-gray-500 ml-2">{gs.programme_name}</span>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-gray-500">{gs.last_row_count || 0} rows</span>
                <span className="text-gray-500">{gs.last_new_rows || 0} new</span>
                <span className={gs.is_enabled ? 'text-green-600 font-medium' : 'text-gray-400'}>
                  {gs.is_enabled ? 'Active' : 'Disabled'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      {grantSources.length === 0 && !loading && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <h4 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
            <Award className="w-4 h-4" />
            Grant Sources
          </h4>
          <p className="text-sm text-amber-600">No grant sources configured</p>
        </div>
      )}

      {/* Company list */}
      <div className="space-y-2">
        {paginatedCompanies.map(company => (
          <motion.div
            key={company.id}
            layout
            className={`border rounded-xl p-4 cursor-pointer transition-colors ${
              selectedCompany?.id === company.id
                ? 'border-primary-300 bg-primary-50/30'
                : 'border-gray-200 hover:border-primary-300 bg-white'
            }`}
            onClick={() => toggleCompany(company)}
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <h4 className="font-medium text-gray-900 truncate">{company.company_name}</h4>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
                  <span className="flex items-center gap-0.5">
                    <MapPin className="w-3 h-3" />
                    {company.city || 'N/A'}{company.voivodeship ? `, ${company.voivodeship}` : ''}
                  </span>
                  <span className="flex items-center gap-0.5">
                    <Hash className="w-3 h-3" />
                    NIP: {company.national_id || 'N/A'}
                  </span>
                  <span className="flex items-center gap-0.5">
                    <Briefcase className="w-3 h-3" />
                    PKD: {company.primary_pkd || 'N/A'}
                  </span>
                </div>
                {/* Industry code tags */}
                {company.industry_codes && company.industry_codes.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(Array.isArray(company.industry_codes) ? company.industry_codes : []).map((code, idx) => (
                      <span
                        key={idx}
                        className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded"
                      >
                        {code}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  company.status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {company.status || 'unknown'}
                </span>
                {selectedCompany?.id === company.id
                  ? <ChevronUp className="w-4 h-4 text-gray-400" />
                  : <ChevronDown className="w-4 h-4 text-gray-400" />
                }
              </div>
            </div>

            {/* Expanded corroboration view */}
            {selectedCompany?.id === company.id && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="mt-4 pt-4 border-t border-gray-200"
                onClick={e => e.stopPropagation()}
              >
                {corroborationLoading && (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
                    <span className="ml-2 text-sm text-gray-500">Loading corroboration…</span>
                  </div>
                )}

                {corroboration?.error && (
                  <p className="text-sm text-red-500">Error: {corroboration.error}</p>
                )}

                {corroboration && !corroboration.error && !corroborationLoading && (
                  <>
                    {/* Company details */}
                    <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                      <div>
                        <span className="text-gray-500">Legal form: </span>
                        <span className="text-gray-900">{corroboration.company?.legal_form || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Employees: </span>
                        <span className="text-gray-900">{corroboration.company?.employee_count || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">KRS: </span>
                        <span className="text-gray-900">{corroboration.company?.register_id || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Data source: </span>
                        <span className="text-gray-900">{corroboration.company?.data_source || 'N/A'}</span>
                      </div>
                    </div>

                    {/* Signal matches */}
                    {corroboration.matches?.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          Signal Matches ({corroboration.matches.length})
                        </h5>
                        <div className="space-y-2">
                          {corroboration.matches.map((m, i) => (
                            <div key={i} className="p-2 bg-gray-50 rounded-lg text-xs">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-gray-900">
                                  {m.signal_title || m.scored_company || 'Signal'}
                                </span>
                                <span className={`px-1.5 py-0.5 rounded text-xs ${
                                  m.signal_layer === 'layer2_grant' ? 'bg-amber-100 text-amber-700' :
                                  m.signal_layer === 'layer2_permit' ? 'bg-blue-100 text-blue-700' :
                                  m.signal_layer === 'layer3_news' ? 'bg-purple-100 text-purple-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {m.signal_layer?.replace('layer', 'L').replace('_', ' ') || 'Unknown'}
                                </span>
                              </div>
                              <div className="flex flex-wrap items-center gap-3 mt-1 text-gray-400">
                                <span>Match: {m.match_type || 'N/A'}</span>
                                <span>Confidence: {Math.round((m.match_confidence || 0) * 100)}%</span>
                                {m.signal_url && (
                                  <a
                                    href={m.signal_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-500 hover:underline flex items-center gap-0.5"
                                    onClick={e => e.stopPropagation()}
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    Source
                                  </a>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Grant history */}
                    {corroboration.grant_history?.length > 0 && (
                      <div>
                        <h5 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-1">
                          <Award className="w-4 h-4" />
                          Grant History ({corroboration.grant_history.length})
                        </h5>
                        <div className="space-y-2">
                          {corroboration.grant_history.map((g, i) => (
                            <div key={i} className="p-2 bg-amber-50 rounded-lg text-xs">
                              <div className="font-medium text-amber-900">
                                {g.project_title || 'Untitled project'}
                              </div>
                              <div className="flex flex-wrap items-center gap-3 mt-1 text-amber-600">
                                <span>{g.programme || 'N/A'}</span>
                                {g.grant_value && (
                                  <span>Grant: {Number(g.grant_value).toLocaleString()} {g.currency || 'PLN'}</span>
                                )}
                                {g.project_value && (
                                  <span>Total: {Number(g.project_value).toLocaleString()} {g.currency || 'PLN'}</span>
                                )}
                                {g.region && <span>{g.region}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {corroboration.matches?.length === 0 && corroboration.grant_history?.length === 0 && (
                      <p className="text-sm text-gray-400 italic">
                        No signal matches or grant history found for this company yet.
                      </p>
                    )}
                  </>
                )}
              </motion.div>
            )}
          </motion.div>
        ))}

        {filteredCompanies.length === 0 && !loading && (
          <div className="text-center py-12 text-gray-400">
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">No companies in the spine yet</p>
            <p className="text-sm mt-1">
              Companies will appear after the intelligence collector runs and scores signals for this profile
            </p>
          </div>
        )}
      </div>

      {/* Pagination controls */}
      {filteredCompanies.length > 0 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            Showing {Math.min((currentPage - 1) * PAGE_SIZE + 1, filteredCompanies.length)}-{Math.min(currentPage * PAGE_SIZE, filteredCompanies.length)} of {filteredCompanies.length} companies
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <span className="text-sm text-gray-500 px-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySpine;
